import { useState, useEffect, useRef, useCallback, useMemo } from "react";

type FirestoreTimestamp = { seconds: number };

export type TimeObj = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

export const useTimer = (timeInSeconds?: FirestoreTimestamp) => {
  const [timeObj, setTimeObj] = useState<TimeObj>({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [isTimeOver, setIsTimeOver] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const formatTimeUnit = (unit: number) => (unit < 10 ? `0${unit}` : `${unit}`);

  const calculateTime = useCallback(() => {
    if (!timeInSeconds) return;

    const targetDate = new Date(timeInSeconds.seconds * 1000).getTime();
    const now = Date.now();
    const distance = targetDate - now;

    if (distance <= 0) {
      clearInterval(timerRef.current!);
      setIsTimeOver(true);
      setTimeObj({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      });
      return;
    }

    const _second = 1000;
    const _minute = 60 * _second;
    const _hour = 60 * _minute;
    const _day = 24 * _hour;

    const days = Math.floor(distance / _day);
    const hours = Math.floor((distance % _day) / _hour);
    const minutes = Math.floor((distance % _hour) / _minute);
    const seconds = Math.floor((distance % _minute) / _second);

    setTimeObj({
      days: formatTimeUnit(days),
      hours: formatTimeUnit(hours),
      minutes: formatTimeUnit(minutes),
      seconds: formatTimeUnit(seconds),
    });

    setIsTimeOver(false);
  }, [timeInSeconds]);

  const startTimer = useCallback(() => {
    if (!timeInSeconds) return;

    clearInterval(timerRef.current!);
    calculateTime();
    timerRef.current = setInterval(calculateTime, 1000);
  }, [calculateTime, timeInSeconds]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);

  // Auto-start timer on mount / timeInSeconds change
  useEffect(() => {
    startTimer();
    return () => clearTimer();
  }, [startTimer, clearTimer]);

  const time = useMemo(
    () =>
      `${timeObj.days || ""}:${timeObj.hours}:${timeObj.minutes}:${
        timeObj.seconds
      }`,
    [timeObj]
  );

  return { time, timeObj, isTimeOver, startTimer, clearTimer };
};
